import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TranslateIcon, SparkleIcon, CheckCircleIcon } from "@phosphor-icons/react";
import { Field, TextInput } from "@/components/CarFormFields";

const STATUS_OPTIONS = [
  { value: "no_english_version", label: "Χωρίς αγγλική έκδοση", color: "bg-[#F3F4F6] text-[#4B5563]" },
  { value: "ai_generated", label: "AI Generated", color: "bg-[#FEF3C7] text-[#92400E]" },
  { value: "manually_reviewed", label: "Manually reviewed", color: "bg-[#DBEAFE] text-[#1E40AF]" },
  { value: "approved", label: "Approved ✓", color: "bg-[#D1FAE5] text-[#065F46]" },
];

/**
 * English fields editor for Polylang dual-language workflow.
 * - Auto-translate via POST /api/cars/{id}/translate (Claude AI).
 * - All EN fields editable.
 * - Translation status + include_in_polylang_export toggle.
 */
export default function EnglishVersionPanel({ form, set }) {
  const [translating, setTranslating] = useState(false);

  const onTranslate = async () => {
    if (!form?.id) return;
    if (!form.description && (!form.equipment || form.equipment.length === 0)) {
      toast.error("Συμπληρώστε πρώτα τα Ελληνικά πεδία (περιγραφή/εξοπλισμός).");
      return;
    }
    setTranslating(true);
    try {
      const r = await api.post(`/cars/${form.id}/translate`);
      const c = r.data;
      set("title_en", c.title_en || "");
      set("variant_en", c.variant_en || "");
      set("description_en", c.description_en || "");
      set("excerpt_en", c.excerpt_en || "");
      set("equipment_en", c.equipment_en || []);
      set("color_exterior_en", c.color_exterior_en || "");
      set("color_interior_en", c.color_interior_en || "");
      set("location_en", c.location_en || "");
      set("fuel_type_en", c.fuel_type_en || "");
      set("transmission_en", c.transmission_en || "");
      set("body_type_en", c.body_type_en || "");
      set("seo_title_en", c.seo_title_en || "");
      set("seo_description_en", c.seo_description_en || "");
      set("translation_status", "ai_generated");
      toast.success("Δημιουργήθηκε η αγγλική έκδοση με AI");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Αποτυχία μετάφρασης";
      toast.error(msg);
    } finally {
      setTranslating(false);
    }
  };

  const currentStatus = form.translation_status || "no_english_version";
  const statusObj = STATUS_OPTIONS.find((s) => s.value === currentStatus) || STATUS_OPTIONS[0];
  const hasContent = !!(form.description_en || (form.equipment_en && form.equipment_en.length));
  const effectiveStatus = !hasContent ? STATUS_OPTIONS[0] : statusObj;

  const equipmentEnText = (form.equipment_en || []).join("\n");
  const onEquipmentEnChange = (e) => {
    const arr = e.target.value.split("\n").map((s) => s.trim()).filter(Boolean);
    set("equipment_en", arr);
  };

  return (
    <div className="space-y-5" data-testid="english-version-panel">
      {/* Header: status + actions */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-[#F9FAFB] border border-black/10 px-4 py-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563]">Polylang · WordPress</div>
          <div className="flex items-center gap-2 mt-1">
            <div className="text-sm font-medium">Αγγλική Έκδοση</div>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 font-mono ${effectiveStatus.color}`} data-testid="translation-status-badge">
              {effectiveStatus.label}
            </span>
          </div>
          <div className="text-xs text-[#4B5563] mt-0.5">
            Δύο ξεχωριστά posts (EL+EN) με ίδιο translation_group_id για Polylang
          </div>
        </div>
        <button
          type="button"
          onClick={onTranslate}
          disabled={translating}
          className="bg-[#0A0A0A] hover:bg-[#1F2937] disabled:opacity-50 text-white px-4 py-2 flex items-center gap-2 text-sm font-medium"
          data-testid="auto-translate-btn"
        >
          {translating ? (<><SparkleIcon size={16} weight="bold" className="animate-pulse" /> Δημιουργία...</>) : (<><TranslateIcon size={16} weight="bold" /> Δημιουργία Αγγλικής Έκδοσης με AI</>)}
        </button>
      </div>

      {/* Workflow controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Translation Status">
          <select
            value={currentStatus}
            onChange={(e) => set("translation_status", e.target.value)}
            disabled={!hasContent}
            className="w-full px-3 py-2 border border-black/20 bg-white outline-none focus:border-[#0055FF] text-sm disabled:opacity-50"
            data-testid="translation-status-select"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Συμπερίληψη στο Polylang export">
          <label className="flex items-center gap-2 px-3 py-2 border border-black/20 bg-white cursor-pointer">
            <input
              type="checkbox"
              checked={form.include_in_polylang_export !== false}
              onChange={(e) => set("include_in_polylang_export", e.target.checked)}
              data-testid="include-polylang-checkbox"
            />
            <span className="text-sm">include_in_polylang_export</span>
            {form.include_in_polylang_export !== false && (
              <CheckCircleIcon size={16} weight="fill" className="text-[#16A34A] ml-auto" />
            )}
          </label>
        </Field>
      </div>

      {/* English fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Post Title (EN)">
          <TextInput value={form.title_en || ""} onChange={(e) => set("title_en", e.target.value)} data-testid="title-en-input" />
        </Field>
        <Field label="Variant (EN)">
          <TextInput value={form.variant_en || ""} maxLength={60} onChange={(e) => set("variant_en", e.target.value)} data-testid="variant-en-input" />
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Fuel Type (EN)">
          <TextInput value={form.fuel_type_en || ""} onChange={(e) => set("fuel_type_en", e.target.value)} data-testid="fuel-en-input" placeholder="Petrol / Diesel / ..." />
        </Field>
        <Field label="Transmission (EN)">
          <TextInput value={form.transmission_en || ""} onChange={(e) => set("transmission_en", e.target.value)} data-testid="trans-en-input" placeholder="Manual / Automatic" />
        </Field>
        <Field label="Body Type (EN)">
          <TextInput value={form.body_type_en || ""} onChange={(e) => set("body_type_en", e.target.value)} data-testid="body-en-input" placeholder="Hatchback / Sedan / SUV" />
        </Field>
        <Field label="Exterior Color (EN)">
          <TextInput value={form.color_exterior_en || ""} onChange={(e) => set("color_exterior_en", e.target.value)} data-testid="color-exterior-en-input" />
        </Field>
        <Field label="Interior Color (EN)">
          <TextInput value={form.color_interior_en || ""} onChange={(e) => set("color_interior_en", e.target.value)} data-testid="color-interior-en-input" />
        </Field>
        <Field label="Location (EN)">
          <TextInput value={form.location_en || ""} onChange={(e) => set("location_en", e.target.value)} data-testid="location-en-input" />
        </Field>
      </div>

      <Field label="Excerpt (EN)">
        <textarea
          value={form.excerpt_en || ""}
          onChange={(e) => set("excerpt_en", e.target.value)}
          rows={2}
          className="w-full px-3 py-2 border border-black/20 bg-white outline-none focus:border-[#0055FF] text-sm"
          placeholder="Short 1-2 sentence summary"
          data-testid="excerpt-en-textarea"
        />
      </Field>

      <Field label="Description (EN)">
        <textarea
          value={form.description_en || ""}
          onChange={(e) => set("description_en", e.target.value)}
          rows={8}
          className="w-full px-3 py-2 border border-black/20 bg-white outline-none focus:border-[#0055FF] text-sm"
          data-testid="description-en-textarea"
        />
      </Field>

      <Field label="Equipment (EN) — μία γραμμή ανά στοιχείο">
        <textarea
          value={equipmentEnText}
          onChange={onEquipmentEnChange}
          rows={Math.min(Math.max((form.equipment_en || []).length + 1, 4), 16)}
          className="w-full px-3 py-2 border border-black/20 bg-white outline-none focus:border-[#0055FF] text-sm font-mono"
          data-testid="equipment-en-textarea"
        />
        <div className="text-[10px] text-[#4B5563] mt-1 font-mono">
          {(form.equipment_en || []).length} items
        </div>
      </Field>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="SEO Title (EN, max 60)">
          <TextInput value={form.seo_title_en || ""} onChange={(e) => set("seo_title_en", e.target.value)} data-testid="seo-title-en-input" />
        </Field>
        <Field label="SEO Description (EN, max 160)">
          <TextInput value={form.seo_description_en || ""} onChange={(e) => set("seo_description_en", e.target.value)} data-testid="seo-desc-en-input" />
        </Field>
      </div>
    </div>
  );
}
